import { create } from "zustand"
import { devtools } from "zustand/middleware"

import { 
    getStatsAppYearsModskill, getStatsAppYearsYaskill, 
    getStatsAppMonthModskill, getStatsAppMonthYaskill,
    getStatsAppDaysModskill, getStatsAppDaysYaskill,
    getStatsContractsYearsModskill, getStatsContractsYearsYaskill,
    getStatsContractsMonthModskill, getStatsContractsMonthYaskill,
    getStatsContractsDaysModskill, getStatsContractsDaysYaskill,
} from "./api"

import { 
    IMonitoringActions, IMonitoringState, 
    IStatsAppYear, IStatsAppMonth, IStatsAppDays,
    IStatsContractsYear, IStatsContractsMonth, IStatsContractsDays
} from "./state.types"


const normalizeContractStatItem = <T extends IStatsContractsYear | IStatsContractsMonth | IStatsContractsDays>(item: T): T => ({
    ...item,
    year: Number(item.year),
    ...(Object.prototype.hasOwnProperty.call(item, 'month') ? { month: Number((item as IStatsContractsMonth | IStatsContractsDays).month) } : {}),
    ...(Object.prototype.hasOwnProperty.call(item, 'day') ? { day: Number((item as IStatsContractsDays).day) } : {}),
    signing: Number(item.signing),
    education: Number(item.education),
    completed: Number(item.completed),
    cancel: Number(item.cancel),
    own: Number(item.own ?? 0),
    total: Number(item.total),
    statusCounts: Array.isArray(item.statusCounts)
        ? item.statusCounts.map((status) => ({
            contractStatusID: Number(status.contractStatusID),
            count: Number(status.count),
        }))
        : [],
}) as T

const normalizeContractStatItems = <T extends IStatsContractsYear | IStatsContractsMonth | IStatsContractsDays>(items: T[] | [] = []): T[] => {
    return items.map((item) => normalizeContractStatItem(item))
}


export const initialStateMonitoring: IMonitoringState = {
    modskill: {
        applications: {
            isLoadingYear: false,
            isLoadingMonth: false,
            isLoadingDays: false,

            itemsYear: [] as IStatsAppYear[],
            itemsMonth: [] as IStatsAppMonth[],
            itemsDays: [] as IStatsAppDays[],
        },

        contracts: {
            isLoadingYear: false,
            isLoadingMonth: false,
            isLoadingDays: false,

            itemsYear: [] as IStatsContractsYear[],
            itemsMonth: [] as IStatsContractsMonth[],
            itemsDays: [] as IStatsContractsDays[],
        }
    },

    yaskill: {
        applications: {
            isLoadingYear: false,
            isLoadingMonth: false,
            isLoadingDays: false,

            itemsYear: [] as IStatsAppYear[],
            itemsMonth: [] as IStatsAppMonth[],
            itemsDays: [] as IStatsAppDays[],
        },

        contracts: {
            isLoadingYear: false,
            isLoadingMonth: false,
            isLoadingDays: false,

            itemsYear: [] as IStatsContractsYear[],
            itemsMonth: [] as IStatsContractsMonth[],
            itemsDays: [] as IStatsContractsDays[],
        }
    }

}

/** Хранилище страницы - Мониторинг */
export const useMonitoring = create<IMonitoringState & IMonitoringActions>()(devtools((set) => ({
    ...initialStateMonitoring,

    /** Сброс хранилища, до начального состояния */
    clearStateMonitoring: () => {
        set(initialStateMonitoring)
    },



    /** Получение статистики заявок по годам ModSkill */
    actionGetStatsAppYearsModskill: async (isLoadingDisplay: boolean = true) => {
        set((state) => ({
            modskill: {
                ...state.modskill,

                applications: {
                    ...state.modskill.applications,
                    isLoadingYear: isLoadingDisplay
                }
            }
        }))


        /** Выполняем запрос */
        const response = await getStatsAppYearsModskill(!isLoadingDisplay)

        // Проверка статуса
        if (response.status === 200) {
            set((state) => ({
                modskill: {
                    ...state.modskill,

                    applications: {
                        ...state.modskill.applications,
                        itemsYear: response.data?.items || []
                    }
                }
            }))
        }

        set((state) => ({
            modskill: {
                ...state.modskill,

                applications: {
                    ...state.modskill.applications,
                    isLoadingYear: false
                }
            }
        }))
        
        return response
    },
    /** Получение статистики заявок по годам YaSkill */
    actionGetStatsAppYearsYaskill: async (isLoadingDisplay: boolean = true) => {
        set((state) => ({
            yaskill: {
                ...state.yaskill,

                applications: {
                    ...state.yaskill.applications,
                    isLoadingYear: isLoadingDisplay
                }
            }
        }))


        /** Выполняем запрос */
        const response = await getStatsAppYearsYaskill(!isLoadingDisplay)

        // Проверка статуса
        if (response.status === 200) {
            set((state) => ({
                yaskill: {
                    ...state.yaskill,

                    applications: {
                        ...state.yaskill.applications,
                        itemsYear: response.data?.items || []
                    }
                }
            }))
        }

        set((state) => ({
            yaskill: {
                ...state.yaskill,

                applications: {
                    ...state.yaskill.applications,
                    isLoadingYear: false
                }
            }
        }))
        
        return response
    },

    
    /** Получение статистики заявок по месяцам ModSkill */
    actionGetStatsAppMonthModskill: async (isLoadingDisplay: boolean = true) => {
        set((state) => ({
            modskill: {
                ...state.modskill,

                applications: {
                    ...state.modskill.applications,
                    isLoadingMonth: isLoadingDisplay
                }
            }
        }))


        /** Выполняем запрос */
        const response = await getStatsAppMonthModskill(!isLoadingDisplay)

        // Проверка статуса
        if (response.status === 200) {
            set((state) => ({
                modskill: {
                    ...state.modskill,

                    applications: {
                        ...state.modskill.applications,
                        itemsMonth: response.data?.items || []
                    }
                }
            }))
        }

        set((state) => ({
            modskill: {
                ...state.modskill,

                applications: {
                    ...state.modskill.applications,
                    isLoadingMonth: false
                }
            }
        }))

        return response
    },
    /** Получение статистики заявок по месяцам YaSkill */
    actionGetStatsAppMonthYaskill: async (isLoadingDisplay: boolean = true) => {
        set((state) => ({
            yaskill: {
                ...state.yaskill,

                applications: {
                    ...state.yaskill.applications,
                    isLoadingMonth: isLoadingDisplay
                }
            }
        }))


        /** Выполняем запрос */
        const response = await getStatsAppMonthYaskill(!isLoadingDisplay)

        // Проверка статуса
        if (response.status === 200) {
            set((state) => ({
                yaskill: {
                    ...state.yaskill,

                    applications: {
                        ...state.yaskill.applications,
                        itemsMonth: response.data?.items || []
                    }
                }
            }))
        }

        set((state) => ({
            yaskill: {
                ...state.yaskill,

                applications: {
                    ...state.yaskill.applications,
                    isLoadingMonth: false
                }
            }
        }))
        
        return response
    },


    /** Получение статистики заявок по дням ModSkill */
    actionGetStatsAppDaysModskill: async (year: number, month: number, isLoadingDisplay: boolean = true) => {
        set((state) => ({
            modskill: {
                ...state.modskill,

                applications: {
                    ...state.modskill.applications,
                    isLoadingDays: isLoadingDisplay
                }
            }
        }))


        /** Выполняем запрос */
        const response = await getStatsAppDaysModskill(year, month, !isLoadingDisplay)

        // Проверка статуса
        if (response.status === 200) {
            set((state) => ({
                modskill: {
                    ...state.modskill,

                    applications: {
                        ...state.modskill.applications,
                        itemsDays: response.data?.items || []
                    }
                }
            }))
        }

        set((state) => ({
            modskill: {
                ...state.modskill,

                applications: {
                    ...state.modskill.applications,
                    isLoadingDays: false
                }
            }
        }))
        
        return response
    },
    /** Получение статистики заявок по дням YaSkill */
    actionGetStatsAppDaysYaskill: async (year: number, month: number, isLoadingDisplay: boolean = true) => {
        set((state) => ({
            yaskill: {
                ...state.yaskill,

                applications: {
                    ...state.yaskill.applications,
                    isLoadingDays: isLoadingDisplay
                }
            }
        }))


        /** Выполняем запрос */
        const response = await getStatsAppDaysYaskill(year, month, !isLoadingDisplay)

        // Проверка статуса
        if (response.status === 200) {
            set((state) => ({
                yaskill: {
                    ...state.yaskill,

                    applications: {
                        ...state.yaskill.applications,
                        itemsDays: response.data?.items || []
                    }
                }
            }))
        }

        set((state) => ({
            yaskill: {
                ...state.yaskill,

                applications: {
                    ...state.yaskill.applications,
                    isLoadingDays: false
                }
            }
        }))
        
        return response
    },

    /** Получение статистики договоров по годам ModSkill */
    actionGetStatsContractsYearsModskill: async (isLoadingDisplay: boolean = true) => {
        set((state) => ({
            modskill: {
                ...state.modskill,

                contracts: {
                    ...state.modskill.contracts,
                    isLoadingYear: isLoadingDisplay
                }
            }
        }))

        const response = await getStatsContractsYearsModskill(!isLoadingDisplay)
        const normalizedItems = normalizeContractStatItems(response.data?.items || [])

        if (response.status === 200) {
            set((state) => ({
                modskill: {
                    ...state.modskill,

                    contracts: {
                        ...state.modskill.contracts,
                        itemsYear: normalizedItems
                    }
                }
            }))
        }

        set((state) => ({
            modskill: {
                ...state.modskill,

                contracts: {
                    ...state.modskill.contracts,
                    isLoadingYear: false
                }
            }
        }))

        return response.status === 200
            ? { ...response, data: response.data ? { ...response.data, items: normalizedItems } : response.data }
            : response
    },
    /** Получение статистики договоров по годам YaSkill */
    actionGetStatsContractsYearsYaskill: async (isLoadingDisplay: boolean = true) => {
        set((state) => ({
            yaskill: {
                ...state.yaskill,

                contracts: {
                    ...state.yaskill.contracts,
                    isLoadingYear: isLoadingDisplay
                }
            }
        }))

        const response = await getStatsContractsYearsYaskill(!isLoadingDisplay)
        const normalizedItems = normalizeContractStatItems(response.data?.items || [])

        if (response.status === 200) {
            set((state) => ({
                yaskill: {
                    ...state.yaskill,

                    contracts: {
                        ...state.yaskill.contracts,
                        itemsYear: normalizedItems
                    }
                }
            }))
        }

        set((state) => ({
            yaskill: {
                ...state.yaskill,

                contracts: {
                    ...state.yaskill.contracts,
                    isLoadingYear: false
                }
            }
        }))

        return response.status === 200
            ? { ...response, data: response.data ? { ...response.data, items: normalizedItems } : response.data }
            : response
    },

    /** Получение статистики договоров по месяцам ModSkill */
    actionGetStatsContractsMonthModskill: async (isLoadingDisplay: boolean = true) => {
        set((state) => ({
            modskill: {
                ...state.modskill,

                contracts: {
                    ...state.modskill.contracts,
                    isLoadingMonth: isLoadingDisplay
                }
            }
        }))

        const response = await getStatsContractsMonthModskill(!isLoadingDisplay)
        const normalizedItems = normalizeContractStatItems(response.data?.items || [])

        if (response.status === 200) {
            set((state) => ({
                modskill: {
                    ...state.modskill,

                    contracts: {
                        ...state.modskill.contracts,
                        itemsMonth: normalizedItems
                    }
                }
            }))
        }

        set((state) => ({
            modskill: {
                ...state.modskill,

                contracts: {
                    ...state.modskill.contracts,
                    isLoadingMonth: false
                }
            }
        }))

        return response.status === 200
            ? { ...response, data: response.data ? { ...response.data, items: normalizedItems } : response.data }
            : response
    },
    /** Получение статистики договоров по месяцам YaSkill */
    actionGetStatsContractsMonthYaskill: async (isLoadingDisplay: boolean = true) => {
        set((state) => ({
            yaskill: {
                ...state.yaskill,

                contracts: {
                    ...state.yaskill.contracts,
                    isLoadingMonth: isLoadingDisplay
                }
            }
        }))

        const response = await getStatsContractsMonthYaskill(!isLoadingDisplay)
        const normalizedItems = normalizeContractStatItems(response.data?.items || [])

        if (response.status === 200) {
            set((state) => ({
                yaskill: {
                    ...state.yaskill,

                    contracts: {
                        ...state.yaskill.contracts,
                        itemsMonth: normalizedItems
                    }
                }
            }))
        }

        set((state) => ({
            yaskill: {
                ...state.yaskill,

                contracts: {
                    ...state.yaskill.contracts,
                    isLoadingMonth: false
                }
            }
        }))

        return response.status === 200
            ? { ...response, data: response.data ? { ...response.data, items: normalizedItems } : response.data }
            : response
    },

    /** Получение статистики договоров по дням ModSkill */
    actionGetStatsContractsDaysModskill: async (year: number, month: number, isLoadingDisplay: boolean = true) => {
        set((state) => ({
            modskill: {
                ...state.modskill,

                contracts: {
                    ...state.modskill.contracts,
                    isLoadingDays: isLoadingDisplay
                }
            }
        }))

        const response = await getStatsContractsDaysModskill(year, month, !isLoadingDisplay)
        const normalizedItems = normalizeContractStatItems(response.data?.items || [])

        if (response.status === 200) {
            set((state) => ({
                modskill: {
                    ...state.modskill,

                    contracts: {
                        ...state.modskill.contracts,
                        itemsDays: normalizedItems
                    }
                }
            }))
        }

        set((state) => ({
            modskill: {
                ...state.modskill,

                contracts: {
                    ...state.modskill.contracts,
                    isLoadingDays: false
                }
            }
        }))

        return response.status === 200
            ? { ...response, data: response.data ? { ...response.data, items: normalizedItems } : response.data }
            : response
    },
    /** Получение статистики договоров по дням YaSkill */
    actionGetStatsContractsDaysYaskill: async (year: number, month: number, isLoadingDisplay: boolean = true) => {
        set((state) => ({
            yaskill: {
                ...state.yaskill,

                contracts: {
                    ...state.yaskill.contracts,
                    isLoadingDays: isLoadingDisplay
                }
            }
        }))

        const response = await getStatsContractsDaysYaskill(year, month, !isLoadingDisplay)
        const normalizedItems = normalizeContractStatItems(response.data?.items || [])

        if (response.status === 200) {
            set((state) => ({
                yaskill: {
                    ...state.yaskill,

                    contracts: {
                        ...state.yaskill.contracts,
                        itemsDays: normalizedItems
                    }
                }
            }))
        }

        set((state) => ({
            yaskill: {
                ...state.yaskill,

                contracts: {
                    ...state.yaskill.contracts,
                    isLoadingDays: false
                }
            }
        }))

        return response.status === 200
            ? { ...response, data: response.data ? { ...response.data, items: normalizedItems } : response.data }
            : response
    },

}), {name: 'StateMonitoring'}))